/**
 * Terrazzo data model (SPEC §5). Everything is an item; views decide how items look.
 *
 * The Y.Doc stores each item as a Y.Map (see doc.ts). The types here describe the
 * plain "snapshot" read model that UI, server projection and MCP all share.
 */

export type ItemId = string
export type UserId = string
/** Who made a change. `agent` = MCP/agent writes; `system` = server housekeeping. */
export type ActorId = UserId | 'agent' | 'system'

/**
 * A card's color (SPEC §7.10). Stored names stay as they were; they're drawn as the nine colors
 * (sand → Saffron, sage → Jade, sky → Ultramarine, rose → Flamingo, slate → Fog, lilac → Violet;
 * azure is Sky). In picker order, warm to cool.
 */
export type Tint = 'none' | 'peach' | 'sand' | 'chartreuse' | 'sage' | 'azure' | 'sky' | 'lilac' | 'rose' | 'slate'
export const TINTS: Tint[] = ['none', 'peach', 'sand', 'chartreuse', 'sage', 'azure', 'sky', 'lilac', 'rose', 'slate']
export const TINT_NAMES: Record<Tint, string> = {
  none: 'No color',
  peach: 'Peach',
  sand: 'Saffron',
  chartreuse: 'Chartreuse',
  sage: 'Jade',
  azure: 'Sky',
  sky: 'Ultramarine',
  lilac: 'Violet',
  rose: 'Flamingo',
  slate: 'Fog',
}

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

/** SPEC §5.5 */
export interface Recurrence {
  freq: 'day' | 'week' | 'month' | 'year'
  interval: number
  byWeekday?: Weekday[]
  byMonthDay?: number
  mode: 'schedule' | 'afterDone'
  until?: string
}

export interface Completion {
  at: number
  by: ActorId
  /** The occurrence date that was completed (recurring items), YYYY-MM-DD. */
  date?: string
}

export type ReminderKind = 'at' | 'morning' | 'daybefore'
export interface Reminder {
  /** Absolute epoch ms. Authoritative for the scheduler. */
  at: number
  sent?: boolean
  /** How `at` was derived from date/time, so it can be recomputed when the date moves. */
  kind?: ReminderKind
}

export type FieldType = 'text' | 'number' | 'money' | 'date' | 'url' | 'select' | 'place'
export interface FieldDef {
  key: string
  type: FieldType
  label: string
  options?: string[]
}
export interface Money {
  cents: number
  currency: 'USD'
}
export interface Place {
  name: string
  address?: string
  lat?: number
  lng?: number
  mapsUrl?: string
}
export type FieldValue = string | number | Money | Place | null

export interface Attachment {
  id: string
  /** R2 key: att/<itemId>/<attId>.<ext> */
  key: string
  thumbKey?: string
  mime: string
  bytes: number
  w?: number
  h?: number
  tags: string[]
  caption?: string
  name?: string
  addedBy: ActorId
  addedAt: number
}

export interface LinkPreview {
  url: string
  title?: string
  siteName?: string
  description?: string
  image?: string
  fetchedAt?: number
}

export type SystemKind = 'inbox' | 'agent-log'

export interface SavedViewFilter {
  tags?: string[]
  tagMode?: 'all' | 'any'
  done?: 'open' | 'done' | 'any'
  from?: string
  to?: string
  /** Rolling window from today: +N = today … N-1 days ahead, -N = the past N days incl. today. Wins over from/to. */
  withinDays?: number
  assignee?: UserId | 'none'
  /** Items inside any of these (cards dragged onto the tab, or picked in its form). */
  ancestors?: ItemId[]
  hasField?: string
  /** Only these kinds (none = all). Saved before labels, this may still hold "event" / "note"; read via normalizeView. */
  kinds?: ItemKind[]
  /** Archived items only (they're otherwise left out): where you find something to restore. */
  archived?: boolean
  /** Legacy single forms of `ancestors` / `kinds`; read via normalizeView. */
  ancestor?: ItemId
  kind?: 'task' | 'event' | 'note'
}
/** How a board-layout view groups its items into cards. `field:<key>` groups by a field's value, `tag:<group>` by a tag group's values. */
export type ViewGroupBy = 'card' | 'tag' | 'assignee' | 'date' | `field:${string}` | `tag:${string}`

export interface SavedView {
  filter: SavedViewFilter
  layout: 'board' | 'list'
  groupBy?: ViewGroupBy
  /** Set = a "Just me" tab, shown only to this user. Absent = shared with everyone. */
  owner?: UserId
  /** Legacy pinned-card tab; read as `filter.ancestor` (see normalizeView). */
  root?: ItemId
  /** How the tab's cards are ordered: dragged into place (default), soonest date first, or A–Z. */
  order?: CardOrder
  /** The tab's own card order (custom): card keys — item ids or group keys — first to last. */
  arrange?: string[]
  /** Subtitles set just for this tab, by card id (each like a card's own `rowFields`). */
  /** A tab's colors for its group cards (by group key: `area/honolulu`, a person's id…). */
  tints?: Record<string, Tint>
  /** A tab's subtitles, per card (by id), per group (`group:<key>`), or for all its cards (`*`). */
  subtitles?: Record<ItemId, string[]>
  /** Its stack of charts, first to last (SPEC §7.9). */
  charts?: ChartSpec[]
  /** Chart ideas already seen or turned down here: idea key → how many items the tab had then. */
  chartIdeas?: Record<string, number>
  /** Its charts hidden (the eye), for everyone. */
  chartsHidden?: boolean
  /** Its total (SPEC §7.12): a detail added up over what it shows, in its top row and on each group's strip. */
  total?: ViewTotal
}

/** A tab's total: which detail, and what counts (everything, what's open, what's done). */
export interface ViewTotal {
  key: string
  adds?: 'all' | 'open' | 'done'
}

// ---------------------------------------------------------------------------
// Charts (SPEC §7.9)

export type ChartStyle = 'ring' | 'bars' | 'mosaic' | 'commits' | 'flow' | 'trail' | 'metro' | 'diagram' | 'wheel'
/** What each piece is: the groupings a tab has, plus each item on its own, or everything as one. */
export type ChartGroup = ViewGroupBy | 'item' | 'none'
/** How big each piece is: how many items, how many are done, or an amount (a money or number detail). */
export type ChartCount = 'items' | 'done' | `amount:${string}`
/** A second split inside each piece ("Color by"). */
export type ChartColorBy = 'none' | 'card' | 'assignee' | 'tag' | `tag:${string}` | `field:${string}`
export type ChartBucket = 'day' | 'week' | 'month'

/**
 * One chart in a stack. Any style takes any of these settings; a style that doesn't use one
 * keeps it for when you switch back.
 */
export interface ChartSpec {
  id: string
  style: ChartStyle
  /** Its own name (unset: named from its settings, like "By type"). */
  title?: string
  /** Cards it shows instead of where it lives (unset: whatever the tab shows). */
  shows?: ItemId[]
  group?: ChartGroup
  /** Grouped by date: each piece is a day, a week or a month. */
  bucket?: ChartBucket
  /** Dates (and Commits): how many days back it reaches. */
  range?: number
  count?: ChartCount
  colorBy?: ChartColorBy
  /** What it leaves out: its numbers, its legend. */
  hide?: Array<'numbers' | 'legend'>
  /** Diagram: which one it shows, `<itemId>/<diagramId>` (SPEC §7.11), lit up by the tab's items. */
  diagram?: string
  /** Its own filter (the tab's Filter fields, just for this chart), on top of what it reads. */
  filter?: SavedViewFilter
  /** Wheel: the items taken off it (until Reset), for everyone. */
  off?: ItemId[]
}

export type CardOrder = 'custom' | 'date' | 'az'

/**
 * Completed items in a checklist: tucked into the "✓ N done" dropdown (collapse), shown
 * checked after the open ones in each group (bottom), or left where they were (inline).
 */
export type DoneStyle = 'collapse' | 'bottom' | 'inline'
export const DONE_STYLES: readonly DoneStyle[] = ['collapse', 'bottom', 'inline']

/** Plain read model of one item. */
export interface ItemSnapshot {
  id: ItemId
  parentId: ItemId | null
  order: string
  dayOrder?: string

  title: string
  /** Plain text of the rich body (empty string when none). */
  bodyText: string

  done: boolean | null
  completedAt?: number
  completedBy?: ActorId
  completions: Completion[]

  date?: string
  time?: string
  endTime?: string
  recur?: Recurrence
  remind?: Reminder[]

  assignee?: UserId | null

  tags: string[]
  props: Record<string, unknown>
  attachments: Attachment[]
  /** Its diagrams (SPEC §7.11), by id; its note's diagram blocks show them. */
  diagrams?: Record<string, import('./diagrams').Diagram>
  link?: LinkPreview

  childFields?: string[]
  labels?: Record<string, string>

  pinned?: boolean
  /** Lives in the Pages tree (sidebar) instead of on Home. */
  page?: boolean
  /** How this card/page lists its checked-off items (default 'collapse'). */
  doneStyle?: DoneStyle
  tint?: Tint
  /** An emoji shown as the item's icon (pages mostly; any item can have one). */
  icon?: string
  /**
   * The subtitle of the rows inside this item, the small line under each (nearest parent that
   * sets it wins; unset = money fields, with totals on parents): field keys, `tag:<group>`, and
   * `hide:date` / `hide:person` / `hide:repeat`. [] = no fields or tags (date etc. still show).
   */
  rowFields?: string[]
  /**
   * A repeating task checked off for this time: it stays done (filed under Done) until this
   * date, its next time, when it reopens with that date.
   */
  reopensOn?: string
  /** The order of its page's parts, arranged just for it (unset = a card's, or automatic). */
  pageOrder?: import('./page-order').PageSection[]
  /** The order of the pages of everything inside it (an item's own wins). */
  childPageOrder?: import('./page-order').PageSection[]
  coverId?: string
  /** Equations for everything inside it (SPEC §7.12), by detail key, as stored. */
  calcInside?: Record<string, string>
  /** The detail its bar shows as its total (usually its own worked-out one). */
  total?: string
  /** Its note's lines of math, in order (read from the note). */
  calcLines?: import('./math').CalcLine[]
  /** How its worked-out details got their answers (filled in by withMath, never stored). */
  calc?: Record<string, import('./math').CalcInfo>

  shareToken?: string | null
  createdBy: ActorId
  updatedBy: ActorId
  createdAt: number
  updatedAt: number
  archivedAt?: number
  trashedAt?: number
  system?: SystemKind
}

/** A task has a checkbox; a label doesn't (it organizes what's under it and is never checked off). */
export type ItemKind = 'task' | 'label'

/** SPEC §5.1 derived semantics — MUST be consistent everywhere. Only the checkbox decides; a date doesn't. */
export function itemKind(item: Pick<ItemSnapshot, 'done'>): ItemKind {
  return item.done !== null ? 'task' : 'label'
}
export const isTask = (i: Pick<ItemSnapshot, 'done'>) => i.done !== null
export const isLabel = (i: Pick<ItemSnapshot, 'done'>) => i.done === null
/** Kinds before labels: "event" (no checkbox, dated) and "note" (no checkbox, undated) are both labels now. */
export const legacyKind = (k: string): ItemKind => (k === 'task' ? 'task' : 'label')

export interface UserInfo {
  name: string
  email: string
  avatarUrl?: string
}

// ---------------------------------------------------------------------------
// Settings (SPEC §9)

export interface HouseholdSettings {
  overdue: 'stay' | 'rollToToday'
  leftoversLookback: number
  nextWeekend: 'sat' | 'sun' | 'mon'
  /** 0 = off */
  autoArchiveDays: 0 | 1 | 3 | 7 | 14 | 30
  daysHorizon: number
  emptyDays: 'line' | 'hidden'
  /** 0 = Sunday, 1 = Monday */
  weekStart: 0 | 1
  timezone: string
  trashRetentionDays: number
  /** Settings → Agent: disable agent writes (read-only mode). */
  agentReadOnly: boolean
  /** Where each detail name shows on an item's page: its quick line or its grid (unset = its default). */
  detailPlacement: Record<string, 'line' | 'grid'>
  /** The Done tab's stack of charts (null: the standard one, a Commits of what got done). */
  doneCharts: ChartSpec[] | null
  /** Chart ideas seen or turned down on the Done tab (as a tab's `chartIdeas`). */
  doneChartIdeas: Record<string, number>
  /** The Done tab's charts hidden (the eye), for everyone. */
  doneChartsHidden: boolean
  /** Google Calendar hidden on Days (the eye), for everyone. */
  googleHidden: boolean
  /** Google calendars hidden on Days, by calendar id (the eye's Sources), for everyone. */
  calendarsHidden: string[]
  /** How Days looks: its list of days, or a month at a time. */
  daysLayout: 'list' | 'month'
}

export const HOUSEHOLD_DEFAULTS: HouseholdSettings = {
  overdue: 'stay',
  leftoversLookback: 14,
  nextWeekend: 'sat',
  autoArchiveDays: 0,
  daysHorizon: 30,
  emptyDays: 'line',
  weekStart: 0,
  timezone: 'America/Los_Angeles',
  trashRetentionDays: 30,
  agentReadOnly: false,
  detailPlacement: {},
  doneCharts: null,
  doneChartIdeas: {},
  doneChartsHidden: false,
  googleHidden: false,
  calendarsHidden: [],
  daysLayout: 'list',
}

export type DoneSort = 'recent' | 'original' | 'az'

export interface UserSettings {
  homeView: string
  theme: 'system' | 'light' | 'dark'
  haptics: boolean
  /** On phones, a box in a list takes a tap to select its task first, then a second to check it (Settings → Just for you). */
  tapFirstToCheck: boolean
  notifyReminders: boolean
  notifyPartner: boolean
  notifyMorningSummary: boolean
  quietStart: string
  quietEnd: string
  captureTarget: ItemId
  doneSort: DoneSort
  /** Settings → Text size, per person: xs · s · m · l · xl. */
  textSize: 'xs' | 's' | 'm' | 'l' | 'xl'
  /** How Google Calendar events look on Days: 'subtle' (quiet gray), 'calendar' (each calendar's own colour), or a card tint. */
  googleColor: string
  /** Days: show Google Calendar events (the "Calendar" switch at the top of Days). */
  daysCalendar: boolean
  /**
   * The time zone this person's phone was last in ('' until it reports one). Their "today", their
   * date-only reminders (Morning of, Day before), quiet hours and Google Calendar follow it;
   * timed tasks and shared things stay on the household's (home) zone.
   */
  timezone: string
}

export const USER_DEFAULTS: UserSettings = {
  homeView: 'board',
  theme: 'system',
  haptics: true,
  tapFirstToCheck: true,
  notifyReminders: true,
  notifyPartner: true,
  notifyMorningSummary: true,
  quietStart: '22:00',
  quietEnd: '07:00',
  captureTarget: 'inbox',
  textSize: 'm',
  doneSort: 'recent',
  googleColor: 'subtle',
  daysCalendar: true,
  timezone: '',
}

export const INBOX_ID = 'inbox'
export const AGENT_LOG_ID = 'agent-log'
export const SYSTEM_IDS = new Set<string>([INBOX_ID, AGENT_LOG_ID])
